import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

// Base URL for frontend API calls (direct to backend)
export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4343';

// Separate axios instance for token refresh (no interceptors -- avoids infinite loop)
const refreshClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// localStorage keys for JWT tokens
const ACCESS_TOKEN_KEY = 'nexmail_access_token';
const REFRESH_TOKEN_KEY = 'nexmail_refresh_token';
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

/* -------------------------------------------------------------------------- */
/* Cached auth token                                                          */
/* -------------------------------------------------------------------------- */

let _cachedToken: string | null = null;

/** Push a fresh access token (called from auth-store on every auth change). */
export function setAuthToken(token: string | null) {
  _cachedToken = token;
}

/** The active impersonation token, if an admin is impersonating a user. */
export function getImpersonationToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(IMPERSONATION_TOKEN_KEY);
}

/**
 * The token every outgoing request should carry.
 *
 * Impersonation takes precedence: while an admin is impersonating, the normal
 * access token still sits in localStorage but represents the *admin's* identity.
 * Sending it would run the request as the admin -- which 401s once that token has
 * aged out (nothing refreshes it during impersonation, since every other call
 * uses the impersonation token) and misattributes quota/audit rows even when it
 * hasn't. Streams must resolve their token the same way axios does.
 */
export function getAuthToken(): string | null {
  return getImpersonationToken() ?? _cachedToken;
}

/**
 * Authenticated fetch for SSE/streaming endpoints that can't use axios.
 * Mirrors the axios interceptor: on 401, refreshes the token and retries once.
 */
export async function authFetchSSE(
  url: string,
  init?: RequestInit
): Promise<Response> {
  const impersonating = getImpersonationToken();
  const token = impersonating ?? _cachedToken;
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    // Refresh mints an access token for the *admin*, which cannot re-establish
    // an impersonated session -- retrying with it would silently run the request
    // as the admin, and a failed refresh would drop impersonation and bounce to
    // sign-in. Surface the 401 so the caller can tell the admin to re-impersonate.
    if (impersonating) {
      return response;
    }

    try {
      const data = await doRefresh();
      headers.set('Authorization', `Bearer ${data.accessToken}`);
      return fetch(url, { ...init, headers });
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      _cachedToken = null;
      window.location.href = '/auth/sign-in';
      return response;
    }
  }

  return response;
}

/* -------------------------------------------------------------------------- */
/* Cross-tab token sync                                                       */
/* -------------------------------------------------------------------------- */

// When another tab refreshes tokens and writes to localStorage, pick them up
// so this tab doesn't try to refresh with a stale (already-consumed) token.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === ACCESS_TOKEN_KEY) {
      _cachedToken = e.newValue;
    }
    // If another tab logged out, follow suit
    if (e.key === ACCESS_TOKEN_KEY && e.newValue === null) {
      _cachedToken = null;
      window.location.href = '/auth/sign-in';
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Refresh mutex                                                              */
/* -------------------------------------------------------------------------- */

// Ensures only one refresh request runs at a time (within this tab).
// All concurrent 401 handlers await the same promise.
let _refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null =
  null;

function doRefresh(): Promise<{ accessToken: string; refreshToken: string }> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    // Re-read from localStorage in case another tab already refreshed
    const freshAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (freshAccessToken && freshAccessToken !== _cachedToken) {
      // Another tab already refreshed -- use its token
      _cachedToken = freshAccessToken;
      return {
        accessToken: freshAccessToken,
        refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || '',
      };
    }

    const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!storedRefreshToken) throw new Error('No refresh token');

    const { data } = await refreshClient.post('/api/auth/refresh', {
      refreshToken: storedRefreshToken,
    });

    if (!data?.accessToken) throw new Error('No access token in response');

    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }
    _cachedToken = data.accessToken;

    return data;
  })().finally(() => {
    _refreshPromise = null;
  });

  return _refreshPromise;
}

/* -------------------------------------------------------------------------- */
/* Axios instance                                                             */
/* -------------------------------------------------------------------------- */

// Create an Axios instance with the base URL
export const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Synchronous request interceptor -- no getSession() call, just use the cache
axiosInstance.interceptors.request.use((config) => {
  // Impersonation token takes precedence -- see getAuthToken().
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor: on 401, refresh via mutex and retry
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Only attempt one retry, and only for 401s that aren't already retries.
    // Skip auth endpoints -- a 401 there means bad credentials, not an expired session.
    // Skip refresh while impersonating -- it mints an admin token that can't
    // renew an impersonated session, so the retry would run as the admin.
    const isAuthEndpoint = original?.url?.startsWith('/api/auth/');
    if (
      error.response?.status === 401 &&
      !original._retried &&
      !isAuthEndpoint &&
      !getImpersonationToken()
    ) {
      original._retried = true;

      try {
        const data = await doRefresh();
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return axiosInstance(original);
      } catch {
        // Refresh failed -- clear tokens and redirect to sign-in
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        _cachedToken = null;
        window.location.href = '/auth/sign-in';
        return Promise.reject(error);
      }
    }

    // Forward error data
    return Promise.reject((error.response && error.response.data) || error);
  }
);

// Type for fetch options
type FetchOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  params?: Record<string, any>;
};

// Orval-style config (what generated code passes)
type OrvalConfig = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

/**
 * Helper to extract URL and config from either format
 */
function parseAuthFetchArgs(
  urlOrConfig: string | OrvalConfig,
  options?: FetchOptions
): { url: string; config: AxiosRequestConfig } {
  // Orval format: first arg is an object with {url, method, params, data}
  if (typeof urlOrConfig === 'object' && urlOrConfig !== null && 'url' in urlOrConfig) {
    const { url, method = 'GET', params, data, headers, signal } = urlOrConfig;
    // Merge per-request headers from BOTH the orval request config (1st arg,
    // e.g. Content-Type) AND the SecondParameter options (2nd arg, where the
    // generated hooks thread `request: { headers }` such as the public preview
    // `x-preview-session` token). The 2nd arg was previously dropped here, so
    // any header passed via `request.headers` never reached the wire.
    const mergedHeaders = { ...(headers ?? {}), ...(options?.headers ?? {}) };
    return {
      url,
      config: {
        method,
        params,
        ...(data && { data }),
        ...(Object.keys(mergedHeaders).length > 0 && { headers: mergedHeaders }),
        ...(signal && { signal }),
      },
    };
  }

  // Legacy format: first arg is a string endpoint
  const { method = 'GET', body, params, headers } = options || {};
  return {
    url: urlOrConfig as string,
    config: {
      method,
      params,
      ...(body && { data: body }),
      ...(headers && { headers }),
    },
  };
}

/**
 * Authenticated fetch function for making API calls to the backend
 * Supports both legacy string format and orval-generated object format
 */
export const authFetch = async <T>(
  urlOrConfig: string | OrvalConfig,
  options?: FetchOptions
): Promise<T> => {
  const { url: endpoint, config } = parseAuthFetchArgs(urlOrConfig, options);

  try {
    const response = await axiosInstance(endpoint, config);
    return response.data;
  } catch (error: any) {
    // Silently handle cancelled requests (normal React Query behavior)
    if (axios.isCancel(error)) {
      throw error;
    }

    console.error('API request failed:', error);
    throw error;
  }
};
